import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { categories as allCategories } from 'utils/categories';
import { getMacroName, getMicroName } from 'utils/getCategoryData';
import CategoryIcon from '../CategoryIcon';

// ---- helpers: normalize to grouped shape ----
const buildMicroToMacroMap = () => {
  const map = new Map();
  const getSubs = (m) =>
    m?.micro || m?.microCategories || m?.children || m?.items || [];
  allCategories.forEach((macro) => {
    getSubs(macro).forEach((micro) => map.set(micro.id, macro.id));
  });
  return map;
};

const normalizeCategories = (input) => {
  if (!input) return [];

  if (Array.isArray(input)) {
    return input
      .filter((x) => {
        if (!x) return false;
        return (
          typeof x.macroCategory === 'number' ||
          typeof x.macroCategoryId === 'number'
        );
      })
      .map((x) => {
        const macro = x.macroCategory ?? x.macroCategoryId;
        const micro = x.microCategory ?? x.microCategoryIds;
        return {
          macroCategory: macro,
          microCategory: Array.isArray(micro) ? [...new Set(micro)] : [],
        };
      });
  }

  if (typeof input === 'object') {
    const microToMacro = buildMicroToMacroMap();
    const macros = Array.isArray(input.macros) ? input.macros : [];
    const micros = Array.isArray(input.micros) ? input.micros : [];

    const grouped = new Map();
    macros.forEach((m) => grouped.set(m, []));
    micros.forEach((mid) => {
      const parent = microToMacro.get(mid);
      if (!parent) return;
      if (!grouped.has(parent)) grouped.set(parent, []);
      grouped.get(parent).push(mid);
    });

    return Array.from(grouped.entries()).map(
      ([macroCategory, microCategory]) => ({
        macroCategory,
        microCategory: [...new Set(microCategory)],
      })
    );
  }

  return [];
};

const CategoryAccordion = ({ categories }) => {
  const [activeCategories, setActiveCategories] = useState([]);

  const grouped = useMemo(() => normalizeCategories(categories), [categories]);

  const toggleCategory = (macroId) => {
    setActiveCategories((prev) =>
      prev.includes(macroId)
        ? prev.filter((id) => id !== macroId)
        : [...prev, macroId]
    );
  };

  if (!grouped.length) return null;

  return (
    <div className="p-4 pl-10 rounded-2xl shadow overflow-y-auto">
      {grouped.map((category, idx) => {
        const macroCategory = allCategories.find(
          (cat) => cat.id === category.macroCategory
        );

        if (!macroCategory) {
          console.error(
            `Macro category with ID ${category.macroCategory} not found.`
          );
          return null;
        }

        const macroId = macroCategory.id;
        const selectedMicroIds = Array.isArray(category.microCategory)
          ? category.microCategory
          : [];

        const macroHasMicro =
          Array.isArray(macroCategory.micro) && macroCategory.micro.length > 0;

        // show chevron only if macro has micros AND there are selected micros to show
        const showChevron = macroHasMicro && selectedMicroIds.length > 0;

        const isOpen = activeCategories.includes(macroId);

        return (
          <div key={`${macroId}-${idx}`}>
            <div
              className={`flex items-center justify-between p-2 ${showChevron ? 'cursor-pointer' : ''}`}
              onClick={showChevron ? () => toggleCategory(macroId) : undefined}
            >
              <div className="flex items-center">
                <CategoryIcon macro={macroId} className="w-8 h-8 mr-2" />
                <span>{getMacroName(macroCategory.id)}</span>
              </div>

              {showChevron &&
                (isOpen ? (
                  <ChevronUpIcon className="h-5 w-5" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5" />
                ))}
            </div>

            {showChevron && isOpen && (
              <div className="pl-7">
                {selectedMicroIds.map((microId, mIdx) => {
                  const microCategory = macroCategory.micro?.find(
                    (m) => m.id === microId
                  );
                  if (!microCategory) {
                    console.error(
                      `Micro category with ID ${microId} not found in ${macroCategory.macro}.`
                    );
                    return null;
                  }
                  return (
                    <div
                      key={`${macroId}-${microId}-${mIdx}`}
                      className="flex items-center py-1"
                    >
                      <CategoryIcon
                        macro={macroId}
                        microCat={microCategory.id}
                        className="w-8 h-8 mr-2"
                      />
                      {getMicroName(microCategory.id)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CategoryAccordion;
